<template>
  <div class="case-download-library">
    <div class="page-content">
      <!-- 顶部 Banner -->
      <div v-if="bannerList.length" class="banner-wrap">
        <van-swipe indicator-color="#FFFFFF" autoplay="5000" class="banner-swipe">
          <van-swipe-item v-for="image in bannerList" :key="image.id">
            <img class="banner-img" :src="getBannerImage(image)" :alt="image.title"/>
          </van-swipe-item>
        </van-swipe>
      </div>

      <!-- 案例下载列表 -->
      <div v-if="list.length" class="list-card">
        <!-- 单条案例 -->
        <div
          v-for="item in list"
          :key="item.id"
          class="list-item"
        >
          <!-- 封面图：点击跳转配置链接 -->
          <div class="item-cover-wrap" @click="handleCaseClick(item)">
            <img
              class="item-cover"
              :src="item.coverMaterialUrl"
              alt=""
            />
          </div>
          <div class="item-content">
            <div class="item-title" @click="handleCaseClick(item)">{{ item.name }}</div>
            <!-- 标签 -->
            <div v-if="item.tagList && item.tagList.length" class="item-tags">
              <span
                v-for="(tag, tagIndex) in item.tagList"
                :key="tagIndex"
                class="item-tag"
              >{{ tag }}</span>
            </div>
            <!-- 简介：超过 4 行省略，可展开/收起 -->
            <div v-if="item.summary" class="item-summary" @click.stop>
              <div
                :ref="'caseDesc-' + item.id"
                class="item-desc"
                :class="{ 'item-desc--clamp': !isDescExpanded(item.id) }"
              >{{ item.summary }}</div>
              <div
                v-if="item.descOverflow"
                class="item-desc-toggle"
                @click.stop="toggleDescExpand(item.id)"
              >
                <span>{{ isDescExpanded(item.id) ? '收起' : '展开' }}</span>
                <img
                  v-if="!isDescExpanded(item.id)"
                  class="item-desc-toggle-icon"
                  src="@/assets/images/course/icon-arrowDown.png"
                  width="12"
                  height="7"
                  alt=""
                />
                <img
                  v-else
                  class="item-desc-toggle-icon"
                  src="@/assets/images/course/icon-arrowUp.png"
                  width="12"
                  height="7"
                  alt=""
                />
              </div>
            </div>
            <!-- 医生 / 医院信息 -->
            <div class="item-footer">
              <img
                class="footer-icon"
                src="@/assets/images/simulator/x_ic_d.svg"
                alt=""
              />
              <span class="footer-text">{{ item.doctorInfo }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 加载更多：点击或滚动触底触发 -->
      <div
        class="load-more"
        :class="{ 'load-more--clickable': isLoadMoreClickable }"
        @click="handleLoadMore"
      >
        <span>{{ loadStatus }}</span>
        <van-icon
          v-if="isLoadMoreClickable"
          name="arrow-down"
          class="load-more-icon"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getKdSimulatorCaseList } from '@/api/simulator'
import { getLibrayBannerByModuleApi } from '@/api/technology'
import withRouteTracking from '@/utils/withRouteTracking'
import { setSha, traceClickURL } from '@/utils/util'

// 列表加载状态枚举（值即展示文案）
const LOAD_STATUS = {
  MORE: '查看更多',
  LOADING: '加载中...',
  FINISHED: '暂无更多数据'
}

const UserProfile = {
  name: 'CaseDownloadLibrary',
  data() {
    return {
      list: [],
      bannerList: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loadStatus: LOAD_STATUS.MORE,
      expandedDescIds: []
    }
  },
  computed: {
    userInfo() {
      return this.$store.getters.userInfo
    },
    // 当前是否处于「查看更多」可点击状态
    isLoadMoreClickable() {
      return this.loadStatus === LOAD_STATUS.MORE
    },
    getBannerImage() {
      return (image) => {
        if (image.pictureMaterial && image.pictureMaterial.url) {
          return image.pictureMaterial.url
        }
        if (image.pictureUrl) {
          return image.pictureUrl
        }
        return ''
      }
    }
  },
  mounted() {
    this.getBannerList()
    this.loadNextPage()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    // 获取顶部 Banner 轮播
    getBannerList() {
      getLibrayBannerByModuleApi('VIRTUAL_SIMULATOR_LIST')
        .then((res) => {
          if (res.code === 0 && res.data && res.data.list) {
            this.bannerList = res.data.list.filter((item) => item.state === '1')
          }
        })
        .catch(() => {})
    },
    // 点击底部「查看更多」加载下一页
    handleLoadMore() {
      if (!this.isLoadMoreClickable) {
        return
      }
      this.loadNextPage()
    },
    // 滚动接近底部时自动加载下一页
    handleScroll() {
      if (!this.isLoadMoreClickable) {
        return
      }

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      const clientHeight = document.documentElement.clientHeight || window.innerHeight
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight

      if (scrollTop + clientHeight >= scrollHeight - 80) {
        this.loadNextPage()
      }
    },
    isDescExpanded(id) {
      return this.expandedDescIds.indexOf(id) !== -1
    },
    toggleDescExpand(id) {
      const wasExpanded = this.isDescExpanded(id)
      const index = this.expandedDescIds.indexOf(id)
      if (index !== -1) {
        this.expandedDescIds.splice(index, 1)
      } else {
        this.expandedDescIds.push(id)
      }
      if (wasExpanded) {
        this.$nextTick(() => {
          this.updateDescOverflow()
        })
      }
    },
    updateDescOverflow() {
      this.$nextTick(() => {
        this.list.forEach((item) => {
          if (!item.summary) {
            this.$set(item, 'descOverflow', false)
            return
          }
          if (this.isDescExpanded(item.id)) {
            return
          }
          const refs = this.$refs['caseDesc-' + item.id]
          const el = refs && (Array.isArray(refs) ? refs[0] : refs)
          if (!el) {
            this.$set(item, 'descOverflow', false)
            return
          }
          const overflow = el.scrollHeight > el.clientHeight + 2
          this.$set(item, 'descOverflow', overflow)
        })
      })
    },
    // 点击标题/封面跳转配置链接页
    handleCaseClick(item) {
      if (!item.redirectUrl) {
        return
      }
      if (item.redirectUrl.indexOf('casesharing') > -1) {
        const openidBindli = `&unionid=${this.userInfo.unionId}&openid=${this.userInfo.iepOpenId}${setSha(`xxbiep${this.userInfo.iepOpenId}`)}`
        const url = `${item.redirectUrl}${openidBindli}`
        localStorage.setItem('functionUrl', item.redirectUrl)
        setTimeout(() => {
          traceClickURL()
        })
        window.location.href = url
        return
      }
      window.location.href = item.redirectUrl
    },
    // 请求并追加下一页数据
    loadNextPage() {
      if (!this.isLoadMoreClickable) {
        return
      }

      this.loadStatus = LOAD_STATUS.LOADING
      getKdSimulatorCaseList({
        pageNum: this.page,
        pageSize: this.pageSize
      })
        .then((res) => {
          const pageList = (res.data && res.data.list) || []
          const total = (res.data && res.data.total) || 0
          this.list = this.list.concat(this.formatCaseList(pageList))
          this.total = total
          if (total === 0 || pageList.length < this.pageSize || this.list.length >= total) {
            this.loadStatus = LOAD_STATUS.FINISHED
          } else {
            this.page += 1
            this.loadStatus = LOAD_STATUS.MORE
          }
          this.updateDescOverflow()
        })
        .catch(() => {
          this.loadStatus = LOAD_STATUS.MORE
        })
    },
    // 格式化接口列表字段
    formatCaseList(list) {
      return (list || []).map((item) => {
        return {
          id: item.id,
          casesId: item.casesId,
          name: item.name,
          summary: item.summary,
          coverMaterialId: item.coverMaterialId,
          coverMaterialUrl: item.coverMaterialUrl,
          tagList: this.normalizeTags(item.tags),
          doctorInfo: item.doctorInfo || '',
          redirectUrl: item.redirectUrl || '',
          state: item.state,
          createTime: item.createTime,
          updateTime: item.updateTime,
          descOverflow: false
        }
      })
    },
    // 标签：接口返回逗号分隔字符串，如「右心房,病变,常规」
    normalizeTags(tags) {
      if (!tags || typeof tags !== 'string') {
        return []
      }
      return tags.split(/[,，]/).map((tag) => tag.trim()).filter(Boolean)
    }
  }
}
export default withRouteTracking(UserProfile)
</script>

<style scoped lang="less">
.case-download-library {
  min-height: 100vh;
  width: 100%;
  background: #f5f7f9;
  box-sizing: border-box;

  .page-content {
    padding: 12px;
    box-sizing: border-box;
  }

  .banner-wrap {
    width: 100%;
    aspect-ratio: 350 / 100;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 12px;

    .banner-swipe {
      width: 100%;
      height: 100%;
    }

    .banner-img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /deep/ .van-swipe {
      height: 100%;
    }

    /deep/ .van-swipe__track {
      height: 100%;
      border-radius: 10px;
    }

    /deep/ .van-swipe-item {
      height: 100%;
    }

    /deep/ .van-swipe__indicator {
      width: 16px;
      height: 3px;
      border-radius: 2px;
    }
  }

  .list-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
  }

  .list-item {
    display: flex;
    padding: 15px 12px;
    box-sizing: border-box;

    &:not(:last-child) {
      border-bottom: 1px solid #E9E9E9;
    }
  }

  .item-cover-wrap {
    flex-shrink: 0;
    width: 84px;
    height: 58px;
    border-radius: 8px;
    overflow: hidden;
    margin-right: 10px;
    cursor: pointer;

    .item-cover {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .item-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .item-title {
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    line-height: normal;
    cursor: pointer;
  }

  .item-tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 4px;

    .item-tag {
      display: inline-block;
      padding: 2px 8px;
      margin-right: 8px;
      margin-bottom: 4px;
      font-size: 10px;
      line-height: 16px;
      color: #FF5B0C;
      background: #FFEFE7;
      border-radius: 4px;
    }
  }

  .item-summary {
    margin-top: 4px;
  }

  .item-desc {
    font-size: 12px;
    line-height: 16px;
    color: #5B5F6A;
    word-break: break-all;

    &--clamp {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
      line-clamp: 4;
    }
  }

  .item-desc-toggle {
    display: flex;
    align-items: center;
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: #EB1700;
    cursor: pointer;

    span {
      margin-right: 4px;
    }

    .item-desc-toggle-icon {
      display: block;
      flex-shrink: 0;
    }
  }

  .item-footer {
    display: flex;
    align-items: center;
    margin-top: 6px;

    .footer-icon {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      margin-right: 4px;
      display: block;
    }

    .footer-text {
      font-size: 10px;
      line-height: 18px;
      color: #000000;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .load-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0 8px;
    font-size: 14px;
    line-height: 20px;
    color: #8c8c8c;
    user-select: none;

    &--clickable {
      cursor: pointer;
    }

    .load-more-icon {
      margin-left: 4px;
      font-size: 14px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/simulator/caseDownloadLibrary.vue